import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { BsFiletypeCsv } from "react-icons/bs";
import { FiCalendar, FiDownload, FiX, FiFileText, FiClock, FiUsers } from "react-icons/fi";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import apiClient from '../../../api/apiClient';

function GenerateReportModal({ isOpen, onClose }) {
  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { token } = useSelector((state) => state.auth);

  // Inject CSS for responsive date range picker
  useEffect(() => {
    const styleId = 'date-range-responsive-styles';
    
    // Check if styles already exist
    if (document.getElementById(styleId)) return;
    
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .date-range-container .rdrCalendarWrapper {
        background: transparent !important;
        width: 100% !important;
        max-width: 100% !important;
      }
      .date-range-container .rdrMonths {
        width: 100% !important;
        display: flex !important;
        justify-content: center !important;
      }
      .date-range-container .rdrMonth {
        width: 100% !important;
        max-width: 350px !important;
        padding: 0 !important;
      }
      .date-range-container .rdrMonthAndYearWrapper {
        justify-content: space-between !important;
        padding: 0 20px !important;
      }
      .date-range-container .rdrDays {
        display: grid !important;
        grid-template-columns: repeat(7, 1fr) !important;
      }
      .date-range-container .rdrDay {
        height: 2.5em !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      .date-range-container .rdrDateDisplayWrapper {
        background: transparent !important;
      }
      .date-range-container .rdrDefinedRangesWrapper {
        display: none !important;
      }
      /* Dark mode styles */
      .dark .date-range-container .rdrMonth {
        background: #1f2937 !important;
      }
      .dark .date-range-container .rdrMonthName {
        color: #f3f4f6 !important;
      }
      .dark .date-range-container .rdrDayNumber span {
        color: #e5e7eb !important;
      }
      .dark .date-range-container .rdrWeekDay {
        color: #9ca3af !important;
      }
      .dark .date-range-container .rdrDayDisabled .rdrDayNumber span {
        color: #4b5563 !important;
      }
      .dark .date-range-container .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span,
      .dark .date-range-container .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
      .dark .date-range-container .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
      .dark .date-range-container .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span {
        color: white !important;
      }
      /* Mobile responsive */
      @media (max-width: 768px) {
        .date-range-container .rdrMonth {
          font-size: 13px !important;
        }
        .date-range-container .rdrMonthAndYearWrapper {
          padding: 0 10px !important;
        }
        .date-range-container .rdrDay {
          height: 2em !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Cleanup function
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Helper function to add one day to a date
  const addOneDay = (date) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 1);
    return newDate;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDaysDifference = (startDate, endDate) => {
    const timeDifference = endDate.getTime() - startDate.getTime();
    return Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;
  };

  const handleDownloadReport = () => {
    let { startDate, endDate } = state[0];
    if (!endDate) {
      setError("Please select a date range before generating the report.");
      return;
    }
    setError("");
    setLoading(true);

    // Add one day to both startDate and endDate
    startDate = addOneDay(startDate);
    endDate = addOneDay(endDate);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    apiClient({
      url: `/api/users/download-report/`,
      method: "GET",
      responseType: "blob",
      params: {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
      },
    })
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `taskers_reviewers_report_from_${formattedStartDate}_to_${formattedEndDate}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        Swal.fire({
          icon: 'success',
          title: 'Report Generated!',
          text: 'Your users report has been downloaded successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          background: 'linear-gradient(135deg, #10b981, #059669)',
          color: 'white'
        });

        onClose();
      })
      .catch((error) => {
        console.error("Error downloading the report:", error);
        Swal.fire({
          icon: 'error',
          title: 'Download Failed',
          text: error.response?.data?.error || "Error downloading the report",
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          color: 'white'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!isOpen) return null;

  const { startDate, endDate } = state[0];
  const daysDifference = getDaysDifference(startDate, endDate);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/30 max-w-4xl w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-emerald-500 to-cyan-500 p-8 text-white">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <FiFileText className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Generate Users Report</h2>
                <p className="text-emerald-100 mt-1">Export detailed user activity data</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Date Selection */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  <FiCalendar className="inline w-4 h-4 mr-2 text-emerald-500" />
                  Select Date Range
                </label>
                <div className="bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-600/50 overflow-hidden">
                  <div className="date-range-wrapper">
                    <DateRange
                      editableDateInputs={true}
                      onChange={(item) => setState([item.selection])}
                      moveRangeOnFirstSelection={false}
                      ranges={state}
                      className="dark:bg-gray-800 dark:text-white"
                      rangeColors={['#10b981']}
                      maxDate={new Date()}
                    />
                  </div>
                  <style>{`
                    .date-range-wrapper {
                      width: 100%;
                      overflow-x: auto;
                    }
                    .date-range-wrapper .rdrCalendarWrapper {
                      background: transparent;
                      width: 100%;
                      font-size: 14px;
                    }
                    .date-range-wrapper .rdrMonth {
                      width: 100%;
                      padding: 0;
                    }
                    .date-range-wrapper .rdrMonthAndYearWrapper {
                      padding: 0 10px;
                    }
                    .date-range-wrapper .rdrMonths {
                      width: 100%;
                    }
                    .date-range-wrapper .rdrDateRangePickerWrapper {
                      width: 100%;
                    }
                    .date-range-wrapper .rdrDateDisplayWrapper {
                      background: transparent;
                    }
                    .date-range-wrapper .rdrDayNumber span {
                      font-size: 13px;
                    }
                    .date-range-wrapper .rdrWeekDay {
                      font-size: 12px;
                    }
                    @media (max-width: 768px) {
                      .date-range-wrapper .rdrCalendarWrapper {
                        font-size: 12px;
                      }
                      .date-range-wrapper .rdrDayNumber span {
                        font-size: 11px;
                      }
                      .date-range-wrapper .rdrWeekDay {
                        font-size: 10px;
                      }
                    }
                  `}</style>
                </div>
                {error && (
                  <div className="mt-3 p-3 bg-red-50/50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                    <p className="text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
                      <span className="text-red-500">⚠️</span>
                      {error}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Report Preview */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <FiUsers className="w-5 h-5 text-emerald-500" />
                  Report Preview
                </h3>
                
                {/* Summary Cards */}
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-emerald-50/50 to-cyan-50/50 dark:from-emerald-900/20 dark:to-cyan-900/20 backdrop-blur-sm rounded-xl p-4 border border-emerald-200/30 dark:border-emerald-700/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Date Range</p>
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                          {formatDate(startDate)} - {formatDate(endDate)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/50 rounded-xl flex items-center justify-center">
                        <FiCalendar className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-sm rounded-xl p-4 border border-blue-200/30 dark:border-blue-700/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Duration</p>
                        <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                          {daysDifference} {daysDifference === 1 ? 'Day' : 'Days'}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center">
                        <FiClock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm rounded-xl p-4 border border-purple-200/30 dark:border-purple-700/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Report Type</p>
                        <p className="text-lg font-bold text-purple-700 dark:text-purple-300">
                          Users Activity Report
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center">
                        <BsFiletypeCsv className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Report Details */}
                {/* <div className="mt-6 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 dark:border-gray-600/50">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Report will include:</h4>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                      User registration details
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                      Activity metrics and statistics
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                      Role-based performance data
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
                      Task completion rates
                    </li>
                  </ul>
                </div> */}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-8 border-t border-gray-200/50 dark:border-gray-600/50 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-all font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleDownloadReport}
              disabled={loading}
              className={`flex-1 px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Generating Report...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <FiDownload className="w-4 h-4" />
                  Generate & Download Report
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default GenerateReportModal;